import { Set as ImmutableSet } from 'immutable';
import { v4 as uuidv4 } from 'uuid';

import { accountsSelectors } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import { filtersActions, filtersTypes, filtersUtils } from 'app-components/flux/filters';
import { getPayeesForTransactions } from 'app-components/flux/payees/payeesSelectors';
import { tagsSelectors } from 'app-components/flux/tags';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';
import type { FilterObject } from 'app-components/flux/filters/filtersTypes';

import { registerNavigationIntent } from 'navigation/intents/registry';
import type { NavigationIntent, NavigationResult } from 'navigation/intents/types';

type TransactionsIntentArgs = {
  payees?: string[];
  categories?: string[];
  accounts?: string[];
  tags?: string[];
  search?: string;
};

type CategoryConfig = {
  id: string;
  name: string;
  type: string;
};

const normalizeInput = (value: string): string => value.trim().toLowerCase();

const getStringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean);
};

const dedupeStrings = (values: string[]): string[] => Array.from(new Set(values));

type IntentFilterInput = {
  categoryIds: string[];
  accountIds: string[];
  tagIds: string[];
  payeeNames: string[];
};

const buildFilterObjectForIntent = ({
  categoryIds,
  accountIds,
  tagIds,
  payeeNames,
}: IntentFilterInput): FilterObject | null => {
  if (!categoryIds.length && !accountIds.length && !tagIds.length && !payeeNames.length) {
    return null;
  }

  let filterObject = filtersTypes.mkFilterObject();
  let numFilterItems = 0;

  if (categoryIds.length) {
    filterObject = filterObject.set('categories', ImmutableSet(categoryIds));
    numFilterItems += categoryIds.length;
  }
  if (accountIds.length) {
    filterObject = filterObject.set('accounts', ImmutableSet(accountIds));
    numFilterItems += accountIds.length;
  }
  if (tagIds.length) {
    filterObject = filterObject.set('tags', ImmutableSet(tagIds));
    numFilterItems += tagIds.length;
  }
  if (payeeNames.length) {
    filterObject = filterObject.set('payeeNames', ImmutableSet(payeeNames));
    numFilterItems += payeeNames.length;
  }

  return filterObject.set('numFilterItems', numFilterItems);
};

const dispatchIntentFilters = (filterObject: FilterObject | null): void => {
  if (!filterObject || !store) {
    return;
  }

  const qcsFilter = filtersUtils.convertFilterObjectToQCSFilter(filterObject);
  const filterItems = qcsFilter.filterItems ?? [];
  if (filterItems.length === 0) {
    return;
  }

  const filterType = qcsFilter.type ?? 'REPORT';
  for (const filterItem of filterItems) {
    const filter = filtersTypes.mkFilter({
      clientId: uuidv4().toUpperCase(),
      type: filterType,
      filterItems: [filterItem],
    });
    store.dispatch(filtersActions.createFilter(filter));
  }
};

const resolveTransactionsIntent = (args: TransactionsIntentArgs | undefined, state: RootState): NavigationResult => {
  const initialRegisterConfig: Record<string, unknown> = {};
  const payload = args ?? {};

  const payees = getStringList(payload.payees);
  let resolvedPayees: string[] = [];
  if (payees.length) {
    const payeesByKey = getPayeesForTransactions(state);
    resolvedPayees = dedupeStrings(payees.map((payee) => payeesByKey.get(normalizeInput(payee)) ?? payee));
    if (resolvedPayees.length) {
      initialRegisterConfig.payees = resolvedPayees;
    }
  }

  const categories = getStringList(payload.categories);
  const resolvedCategories: CategoryConfig[] = [];
  if (categories.length) {
    const categoriesById = categoriesSelectors.getCategoriesById(state);
    for (const name of categories) {
      const normalized = normalizeInput(name);
      const match = categoriesById.find((category) => category?.name?.toLowerCase?.() === normalized);
      if (match?.id) {
        resolvedCategories.push({
          id: match.id,
          name: match.name ?? name,
          type: match.type ?? match.categoryType ?? 'EXPENSE',
        });
      }
    }
    if (resolvedCategories.length) {
      initialRegisterConfig.categories = resolvedCategories;
    }
  }

  const accounts = getStringList(payload.accounts);
  let resolvedAccountIds: string[] = [];
  if (accounts.length) {
    const accountsById = accountsSelectors.getAccountsById(state);
    resolvedAccountIds = dedupeStrings(
      accounts
        .map((name) => {
          const normalized = normalizeInput(name);
          const match = accountsById.find((account) => account?.name?.toLowerCase?.() === normalized);
          return match?.id ?? '';
        })
        .filter(Boolean),
    );
    if (resolvedAccountIds.length) {
      initialRegisterConfig.accounts = resolvedAccountIds;
    }
  }

  const tags = getStringList(payload.tags);
  let resolvedTagIds: string[] = [];
  if (tags.length) {
    const tagsById = tagsSelectors.getTags(state);
    resolvedTagIds = dedupeStrings(
      tags
        .map((name) => {
          const normalized = normalizeInput(name);
          const match = tagsById.find((tag) => tag?.name?.toLowerCase?.() === normalized);
          return match?.id ?? '';
        })
        .filter(Boolean),
    );
    if (resolvedTagIds.length) {
      initialRegisterConfig.tags = resolvedTagIds;
    }
  }

  if (typeof payload.search === 'string' && payload.search.trim()) {
    initialRegisterConfig.filterExpression = payload.search.trim();
  }

  const intentFilterObject = buildFilterObjectForIntent({
    categoryIds: dedupeStrings(resolvedCategories.map((category) => category.id)),
    accountIds: resolvedAccountIds,
    tagIds: resolvedTagIds,
    payeeNames: resolvedPayees,
  });
  dispatchIntentFilters(intentFilterObject);

  return Object.keys(initialRegisterConfig).length > 0
    ? {
        type: 'navigate',
        payload: {
          path: '/transactions',
          state: { initialRegisterConfig },
        },
      }
    : {
        type: 'navigate',
        payload: { path: '/transactions' },
      };
};

const transactionsIntent: NavigationIntent<TransactionsIntentArgs, RootState> = {
  name: 'transactions',
  description: 'List of all financial transactions with search and filtering capabilities.',
  params: {
    definitions: [
      { name: 'payees', type: 'string[]', description: 'Filter by payee names' },
      { name: 'categories', type: 'string[]', description: 'Filter by category names' },
      { name: 'accounts', type: 'string[]', description: 'Filter by account names' },
      { name: 'tags', type: 'string[]', description: 'Filter by tag names' },
      { name: 'search', type: 'string', description: 'Search text to filter transactions' },
    ],
  },
  resolver: resolveTransactionsIntent,
};

registerNavigationIntent(transactionsIntent);
